import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { push } from 'connected-react-router';
import delay, { delayTime } from '../../utils/delay';
import axiosInstance from '../../utils/axiosInstance';
import { componentsEnum } from '../../utils/types';
import normalize, { normalizeInteractiblePlantsByBeeId } from '../../utils/normalize';
import { setErrorMessage } from '../genericError';
import { restartSession } from '../session';
import {captureExceptionInProd} from "../../utils/sentry";

// I'd rather have this in the index.js, however, this causes a cirular
// dependency
export const getInteractiblePlantsForBees = createAsyncThunk(
  'bees/getInteractiblePlantsForBees',
  async (beeIds, { dispatch }) => {
    let interactiblePlantsByBeeId;

    try {
      const result = await axiosInstance.get('bees/get-interactible-plants', {
        params: {
          beeIds: JSON.stringify(beeIds),
        },
      });

      interactiblePlantsByBeeId = result.data.data.interactiblePlantsByBeeId;
    } catch (err) {
      dispatch(
        push(componentsEnum.properties[componentsEnum.selectArea].route),
      );

      if (!err.response) {
        return dispatch(setErrorMessage('Server onbereikbaar'));
      }

      dispatch(setErrorMessage(err.response.data.message));

      return null;
    }

    const normalizedInteractiblePlantsByBeeId = normalizeInteractiblePlantsByBeeId(
      interactiblePlantsByBeeId,
    );

    return {
      interactiblePlantsByBeeId: normalizedInteractiblePlantsByBeeId,
    };
  },
);

export const approveSelectedArea = createAsyncThunk(
  'area/fetchWithCoords',
  async (coords, { dispatch, rejectWithValue }) => {
    dispatch(push('/loading'));
    const startTime = performance.now();

    let presentBees;

    try {
      const result = await axiosInstance.get('bees/get-in-area', {
        params: {
          area: JSON.stringify(coords),
        },
      });

      presentBees = result.data.data.beesInArea;
    } catch (err) {
      dispatch(
        push(componentsEnum.properties[componentsEnum.selectArea].route),
      );

      // No response means the server is not running
      if (!err.response) {
        captureExceptionInProd(error);

        return dispatch(setErrorMessage('Server onbereikbaar'));
      }

      return rejectWithValue(err.response.data.message);
    }

    if (presentBees.length === 0) {
      dispatch(
        push(componentsEnum.properties[componentsEnum.selectArea].route),
      );
      return rejectWithValue(
        'Er zijn geen bijen gemodelleerd voor het geselecteerde gebied.',
      );
    }

    const {
      values: beesById,
      ids: beeIds,
    } = normalize(presentBees);

    dispatch(getInteractiblePlantsForBees(beeIds));

    const endTime = performance.now();
    const elapsedMs = endTime - startTime;
    await delay(delayTime - elapsedMs);

    dispatch(
      push(componentsEnum.properties[componentsEnum.beeSelectionMethod].route),
    );

    return {
      coords,
      beesById,
      beeIds,
    };
  },
);

const initialState = {
  isTouched: false,
  error: null,
  coords: null,
  approved: null,
};

export const area = createSlice({
  name: 'area',
  initialState,
  reducers: {
    setCoords: (state, action) => {
      state.coords = action.payload;
    },
    setApproved: (state, action) => {
      state.approved = action.payload;
    },
    setIsTouched: (state, action) => {
      state.isTouched = action.payload;
    },
  },
  extraReducers: {
    [restartSession.fulfilled]: () => initialState,
    [approveSelectedArea.fulfilled]: (state, action) => {
      state.coords = action.payload.coords;
      state.error = null;
      state.approved = true;
    },
    [approveSelectedArea.rejected]: (state, action) => {
      state.approved = false;
      // action.payload will be populated if we returned a custom error with
      // rejectWithValue
      if (action.payload) {
        state.error = action.payload;
      } else {
        state.error = action.error.message;
      }
    },
  },
});

export const {
  reset,
  setIsTouched,
  setCoords,
} = area.actions;

export const selectUuid = (state) => state.area.uuid;
export const selectCoords = (state) => state.area.coords;
export const selectApproved = (state) => state.area.approved;
export const selectIsTouched = (state) => state.area.isTouched;

export default area.reducer;
